<script lang="ts" setup>
import { computed } from 'vue';
import { ApiError } from '@/helpers/api-error';

interface Props {
  // Type of skeleton shape
  type: 'table' | 'grid' | 'panel' | 'text' | 'form' | 'header';

  // Animation style (Bootstrap 5 placeholder animations)
  animation?: 'wave' | 'glow';

  // Error state — when truthy, shows an inline error instead of the skeleton
  error?: unknown;
  errorMessage?: string;

  // Table options
  rows?: number;
  columns?: number;

  // Grid options
  cards?: number;
  gridColumns?: number; // Number of columns in grid layout

  // Panel/Text options
  lines?: number;
  showHeader?: boolean;

  // Form options
  fields?: number;
}

const props = withDefaults(defineProps<Props>(), {
  animation: 'wave',
  rows: 3,
  columns: 4,
  cards: 6,
  gridColumns: 3,
  lines: 3,
  showHeader: false,
  fields: 6
});

const emit = defineEmits<{
  retry: [];
}>();

// Error display — derives icon and message from error type
const errorIcon = computed(() => {
  if (!props.error) return 'bi-exclamation-circle';
  if (props.error instanceof ApiError) {
    const status = props.error.problem.status;
    if (!status) return 'bi-wifi-off';
    if (status === 403) return 'bi-shield-lock';
    if (status === 404) return 'bi-search';
    if (status >= 500) return 'bi-exclamation-triangle';
  }
  return 'bi-exclamation-circle';
});

const errorText = computed(() => {
  if (props.errorMessage) return props.errorMessage;
  if (props.error instanceof ApiError) return props.error.userMessage;
  if (props.error instanceof Error) return props.error.message;
  return 'Failed to load data';
});

// Animation class based on prop
const animationClass = computed(() =>
  props.animation === 'glow' ? 'placeholder-glow' : 'placeholder-wave'
);

// Generate varied widths for more natural look
const widthClasses = ['col-4', 'col-5', 'col-6', 'col-7', 'col-8', 'col-9', 'col-10'];

function randomWidth(seed: number): string {
  // Use seed for consistent rendering (avoids flicker on re-render)
  return widthClasses[seed % widthClasses.length];
}

// Grid column classes based on gridColumns prop
const gridColClasses = computed(() => {
  const cols = props.gridColumns;
  return {
    'row-cols-1': true,
    'row-cols-sm-2': cols >= 2,
    'row-cols-md-2': cols >= 2,
    'row-cols-lg-3': cols >= 3,
    'row-cols-xl-4': cols >= 4
  };
});
</script>

<template>
  <!-- Error state — replaces skeleton when error is truthy -->
  <div v-if="error" class="text-center py-5 text-body-secondary">
<!--    <h4 class="error-title mb-2">{{ displayTitle }}</h4>-->
    <i :class="errorIcon" class="fs-1 d-block mb-3"></i>
    <p class="mb-3">{{ errorText }}</p>
    <button class="bb-btn-secondary-cta" @click="emit('retry')">
      <i class="bi bi-arrow-clockwise me-1"></i>Retry
    </button>
  </div>

  <!-- Table skeleton -->
  <table v-else-if="type === 'table'" class="table" :class="animationClass" aria-hidden="true">
    <thead>
      <tr>
        <th v-for="c in columns" :key="c" scope="col">
          <span class="placeholder" :class="randomWidth(c)"></span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="r in rows" :key="r">
        <td v-for="c in columns" :key="c">
          <span class="placeholder" :class="randomWidth(r + c)"></span>
        </td>
      </tr>
    </tbody>
  </table>

  <!-- Grid/Card skeleton -->
  <div v-else-if="type === 'grid'" class="row g-3" :class="gridColClasses" aria-hidden="true">
    <div v-for="i in cards" :key="i" class="col">
      <div class="card h-100" :class="animationClass">
        <div class="card-body">
          <h5 class="card-title">
            <span class="placeholder" :class="randomWidth(i)"></span>
          </h5>
          <p class="card-text">
            <span class="placeholder" :class="randomWidth(i + 1)"></span>
            <span class="placeholder" :class="randomWidth(i + 2)"></span>
          </p>
          <div class="mt-auto pt-2">
            <span class="placeholder col-4"></span>
          </div>
        </div>
        <div class="card-footer bg-transparent border-top-0">
          <span class="placeholder col-6"></span>
        </div>
      </div>
    </div>
  </div>

  <!-- Panel skeleton -->
  <div v-else-if="type === 'panel'" :class="animationClass" aria-hidden="true">
    <h5 v-if="showHeader" class="mb-3">
      <span class="placeholder col-4"></span>
    </h5>
    <p v-for="i in lines" :key="i" class="mb-2">
      <span class="placeholder" :class="randomWidth(i)"></span>
    </p>
  </div>

  <!-- Form skeleton (horizontal form layout) -->
  <div v-else-if="type === 'form'" :class="animationClass" aria-hidden="true">
    <div v-for="i in fields" :key="i" class="row mb-3">
      <div class="col-sm-2 col-form-label">
        <span class="placeholder" :class="randomWidth(i + 2)"></span>
      </div>
      <div class="col-sm-10">
        <span class="placeholder col-12 form-control border-0"></span>
      </div>
    </div>
    <div>
      <span class="placeholder btn btn-primary disabled col-1"></span>
    </div>
  </div>

  <!-- Header skeleton (page header with breadcrumbs, title, actions, tabs) -->
  <div v-else-if="type === 'header'" :class="animationClass" aria-hidden="true">
    <!-- Breadcrumbs -->
    <div class="mb-2">
      <span class="placeholder col-3"></span>
    </div>
    <!-- Title + actions row -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <span class="placeholder col-4" style="height: 2rem;"></span>
      <div class="d-flex gap-2">
        <span class="placeholder rounded" style="width: 70px; height: 38px;"></span>
        <span class="placeholder rounded" style="width: 45px; height: 38px;"></span>
      </div>
    </div>
  </div>

  <!-- Text skeleton (simple lines) -->
  <div v-else :class="animationClass" aria-hidden="true">
    <span
      v-for="i in lines"
      :key="i"
      class="placeholder d-block mb-2"
      :class="randomWidth(i)"
    ></span>
  </div>
</template>

<style lang="scss" scoped>
/* Apply rounded corners globally to all placeholders */
.placeholder {
  border-radius: var(--bs-border-radius);
}
</style>
